import { Routes, Route } from 'react-router-dom';
import { useEffect, useState } from 'react';
import { useStore } from '@/store';
import { initializeFirebase, checkRedirectResult } from '@/lib/firebase';
import { initializeAnalytics } from '@/lib/analytics';
import Dashboard from '@/pages/Dashboard';
import Settings from '@/pages/Settings';
import Invite from '@/pages/Invite';
import Admin from '@/pages/Admin';
import Layout from '@/components/Layout';

function App() {
  const { initFromLocalStorage, initFromUrl, setAuth } = useStore();
  const [firebaseReady, setFirebaseReady] = useState(false);
  const [firebaseError, setFirebaseError] = useState<string | null>(null);
  
  useEffect(() => {
    // Initialize Firebase on app startup
    initializeFirebase()
      .then(async () => {
        setFirebaseReady(true);
        // Initialize analytics after Firebase is ready
        initializeAnalytics();
        // Check for redirect result (if user was redirected from Google sign-in)
        const user = await checkRedirectResult();
        if (user) {
          console.log('User signed in via redirect:', user.displayName);
          setAuth({
            isAuthenticated: true,
            user: {
              uid: user.uid,
              email: user.email || '',
              displayName: user.displayName || '',
              photoURL: user.photoURL || '',
            },
          });
        }
      })
      .catch((error) => {
        console.error('Firebase initialization failed:', error);
        setFirebaseError(error.message);
        // Still allow app to load even if Firebase fails
        setFirebaseReady(true);
      });
  }, [setAuth]);

  useEffect(() => {
    if (!firebaseReady) return;
    
    // First check URL for shared data
    const urlParams = new URLSearchParams(window.location.search);
    const sharedData = urlParams.get('d');
    
    if (sharedData) {
      initFromUrl(sharedData);
    } else {
      // Load from localStorage
      initFromLocalStorage();
    }
  }, [firebaseReady, initFromLocalStorage, initFromUrl]);

  // Show error if Firebase failed to initialize
  if (firebaseError && !firebaseReady) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-red-50 dark:bg-red-950">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-red-700 dark:text-red-400 mb-2">Firebase Configuration Error</h1>
          <p className="text-red-600 dark:text-red-400">{firebaseError}</p>
          <p className="text-gray-600 dark:text-gray-400 mt-4">Please check server logs and try again.</p>
        </div>
      </div>
    );
  }

  return (
    <Routes>
      <Route path="/" element={<Layout />}>
        <Route index element={<Dashboard />} />
        <Route path="settings" element={<Settings />} />
      <Route path="/admin" element={<Admin />} />
        <Route path="invite/:code" element={<Invite />} />
      </Route>
    </Routes>
  );
}

export default App;
